/**
 * Spec 040: a staff member's console in the tasker — the same screen as on its PC, drawn here
 * while somebody watches it, and the keys a person presses in it. «Открыть консоль» asks the PC
 * to send it; while it is open the tasker confirms it every 20 seconds, and 60 seconds without —
 * the PC stops. What is typed here goes into the console as typed on the PC: only a person types,
 * never an agent (the server refuses an agent's keys).
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";

import { ApiError } from "@/api/client";
import { Console, type ConsoleKey, type ConsoleState } from "@/api/feed";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import { EmptyNote } from "@/kit";
import { Screen, type Style } from "@/lib/terminal";

const CONFIRM_EVERY = 20_000;
const WAIT = 20;
const ENTER_AFTER = 150;
const KEYS: { key: ConsoleKey; label: string; hint: string }[] = [
  { key: "esc", label: "Esc", hint: "прервать ход" },
  { key: "up", label: "↑", hint: "выше" },
  { key: "down", label: "↓", hint: "ниже" },
  { key: "enter", label: "Enter", hint: "выбрать" },
  { key: "ctrl_c", label: "Ctrl+C", hint: "прервать" },
];

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function said(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return "Нет связи с таскером: консоль появится, когда связь вернётся.";
}

function css(s: Style): CSSProperties {
  const fg = s.inverse ? s.bg ?? "#111111" : s.fg ?? undefined;
  const bg = s.inverse ? s.fg ?? "#e5e5e5" : s.bg ?? undefined;
  return {
    color: fg,
    backgroundColor: bg,
    fontWeight: s.bold ? 700 : undefined,
    opacity: s.dim ? 0.7 : undefined,
    fontStyle: s.italic ? "italic" : undefined,
    textDecoration: s.underline ? "underline" : undefined,
  };
}

/** The screen drawn in monospace; the font follows the width, the size is the PC's (two
 * viewers never argue about it). New output scrolls down unless the reader scrolled up. */
function TerminalView({ screen, frame }: { screen: Screen | null; frame: number }) {
  const box = useRef<HTMLDivElement | null>(null);
  const bottom = useRef(true);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);
  useEffect(() => {
    const el = box.current;
    if (el && bottom.current) el.scrollTop = el.scrollHeight;
  }, [frame]);
  const cols = screen?.cols ?? 120;
  const font = width ? Math.max(7, Math.min(14, (width - 24) / (cols * 0.6))) : 12;
  const lines = screen ? screen.view() : [];
  return (
    <div
      ref={box}
      role="log"
      aria-label="Консоль сотрудника"
      data-frame={frame}
      onScroll={(e) => {
        const el = e.currentTarget;
        bottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
      }}
      className="max-h-[70vh] min-h-[12rem] overflow-auto rounded-card bg-[#111111] p-3 font-mono text-[#e5e5e5]"
      style={{ fontSize: `${font}px`, lineHeight: 1.25 }}
    >
      {lines.length === 0 ? (
        <p className="text-[#9a9a9a]">Жду вывод консоли с ПК…</p>
      ) : (
        lines.map((runs, i) => (
          <div key={i} className="whitespace-pre">
            {runs.length ? runs.map((r, j) => <span key={j} style={css(r.style)}>{r.text}</span>) : " "}
          </div>
        ))
      )}
    </div>
  );
}

export function LiveConsole({ agentId, running }: { agentId: string; running: boolean }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<ConsoleState | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [ended, setEnded] = useState<string | null>(null);
  const [frame, setFrame] = useState(0);
  const [line, setLine] = useState("");
  const [sending, setSending] = useState(false);
  const screen = useRef<Screen | null>(null);

  useEffect(() => {
    if (!open) return;
    let stop = false;
    const controller = new AbortController();
    let keepAlive: ReturnType<typeof setInterval> | undefined;
    let end = -1;
    screen.current = null;
    setEnded(null);
    const watch = async (): Promise<boolean> => {
      try {
        const s = await Console.watch(agentId);
        if (stop) return false;
        setState(s);
        setProblem(null);
        return true;
      } catch (e) {
        if (!stop) setProblem(said(e));
        return false;
      }
    };
    const run = async () => {
      while (!stop && !(await watch())) await sleep(5_000);
      if (stop) return;
      keepAlive = setInterval(() => void watch(), CONFIRM_EVERY);
      while (!stop) {
        try {
          const chunk = await Console.read(agentId, Math.max(end, 0), WAIT, controller.signal);
          if (stop) return;
          const size = chunk.size ?? { cols: 120, rows: 30 };
          if (!screen.current || end < 0 || chunk.begin > end) {
            // The first look, or the PC's tail moved past what was shown: from what is kept.
            screen.current = new Screen(size.cols, size.rows);
          } else if (screen.current.cols !== size.cols || screen.current.rows !== size.rows) {
            screen.current.resize(size.cols, size.rows);
          }
          if (chunk.data) screen.current.write(chunk.data);
          end = chunk.end;
          setFrame((n) => n + 1);
          if (!chunk.alive) {
            setEnded(`Консоль на ПК закрылась${chunk.exit_code != null ? ` · код ${chunk.exit_code}` : ""}.`);
            return;
          }
        } catch (e) {
          if (stop) return;
          if (e instanceof ApiError && e.status === 409 && e.info?.error === "not_watched") {
            await watch();
            continue;
          }
          setProblem(said(e));
          await sleep(5_000);
          await watch();
        }
      }
    };
    void run();
    return () => {
      stop = true;
      controller.abort();
      if (keepAlive) clearInterval(keepAlive);
    };
  }, [open, agentId]);

  const press = async (keys: ConsoleKey[], data: string | null = null): Promise<boolean> => {
    setSending(true);
    try {
      await Console.input(agentId, data, keys);
      return true;
    } catch (e) {
      toast.error(said(e));
      return false;
    } finally {
      setSending(false);
    }
  };

  const send = async () => {
    const text = line;
    if (!text) return;
    setLine("");
    // The text, then Enter a moment later: the console takes it as typed, not as a paste.
    if (await press([], text)) {
      await sleep(ENTER_AFTER);
      await press(["enter"]);
    } else setLine(text);
  };

  if (!running) {
    return <EmptyNote>Сотрудник сейчас не запущен — консоли нет. Что он делал — в ленте.</EmptyNote>;
  }
  if (!open) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="text-body text-p-text-muted">
          Тот же экран, что на ПК. Пока консоль открыта, ПК передаёт её сюда; печатать в неё может только человек.
        </p>
        <Button variant="strong" onClick={() => setOpen(true)}>Открыть консоль</Button>
      </div>
    );
  }
  const alive = Boolean(state?.alive) && !ended;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-p-text-muted">
        {state && <span>ПК «{state.device_name}»{state.online ? "" : " · не на связи"}</span>}
        {state && state.viewers.length > 0 && <span>смотрят: {state.viewers.join(", ")}</span>}
        <button type="button" className="ml-auto min-h-11 font-semibold text-p-link" onClick={() => setOpen(false)}>
          Закрыть консоль
        </button>
      </div>
      {problem && <p className="text-meta text-p-blocked" role="status">{problem}</p>}
      {ended && <p className="text-meta text-p-text" role="status">{ended}</p>}
      <TerminalView screen={screen.current} frame={frame} />
      <div className="flex flex-wrap gap-2" aria-label="Клавиши консоли">
        {KEYS.map((k) => (
          <Button key={k.key} size="compact" variant="secondary" title={k.hint} disabled={sending || !alive} onClick={() => void press([k.key])}>
            {k.label}
          </Button>
        ))}
      </div>
      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input
          value={line}
          onChange={(e) => setLine(e.target.value)}
          placeholder="Ввод в консоль…"
          aria-label="Ввод в консоль"
          autoComplete="off"
          disabled={!alive}
          className="min-h-11 min-w-0 flex-1 rounded-compact border border-p-border-strong bg-p-surface px-3 font-mono text-[15px] text-p-text placeholder:text-p-text-muted focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-p-focus"
        />
        <Button type="submit" size="compact" variant="strong" disabled={!line || sending || !alive}>Отправить</Button>
      </form>
      <p className="text-meta text-p-text-muted">
        Это нажатия прямо в консоль сотрудника, как на ПК. Поправить его словами — «Написать сотруднику» в ленте: там подпись, кто пишет.
      </p>
    </div>
  );
}
